/* eslint-disable @typescript-eslint/naming-convention */
import {useCallback, useMemo} from 'react';
import {useRouter} from 'next/router';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {EventCategory, EventNames, TOwoxPage} from 'modules/google-analytics/constants';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';

const LOYALTY_RESTRICTION_EVENT_ACTION = {fix_price: 'informerFP', third_party_assortment: 'informerNoBonuses'};

const LOYALTY_RESTRICTION_EVENT_LABELS = {
    fix_price: {
        ICON_PDP_SHOW: 'informerFPiconPDPShow',
        ICON_PDP_CLICK: 'informerFPiconPDPClick',
        POPUP_PDP_SHOW: 'informerFPpopupPDPShow',
        POPUP_PDP_CLOSE: 'informerFPpopupPDPClose',
    },
    third_party_assortment: {
        ICON_PDP_SHOW: 'informerNoBonusesiconPDPShow',
        ICON_PDP_CLICK: 'informerNoBonusesiconPDPClick',
        POPUP_PDP_SHOW: 'informerNoBonusespopupPDPShow',
        POPUP_PDP_CLOSE: 'informerNoBonusespopupPDPClose',
    },
} as const;

type TLoyaltyRestrictionEventContextItem = {
    pageType: TOwoxPage;
    pagePath: string;
    productId: string;
    productName: string;
};

interface IParams {
    product?: Pick<IProduct, 'id' | 'title' | 'price'> | null;
}

interface IResult {
    eventContext: TLoyaltyRestrictionEventContextItem[] | null;
    handleLoyaltyRestrictionIconInView: (inView: boolean) => void;
    handleLoyaltyRestrictionIconClick: () => void;
    handleLoyaltyRestrictionPopupShow: () => void;
    handleLoyaltyRestrictionPopupClose: () => void;
}

const normalizePagePath = (asPath: string): string => {
    return asPath.split('?')[0]?.split('#')[0] ?? '';
};

export const useLoyaltyRestrictionInformerGtag = ({product}: IParams): IResult => {
    const router = useRouter();

    const {eventLocation} = useGtagEventLocation();

    const eventContext = useMemo((): TLoyaltyRestrictionEventContextItem[] | null => {
        const pagePath = normalizePagePath(router.asPath ?? '');

        const productId = product?.id !== undefined && null !== product?.id ? String(product.id) : '';
        const productName = product?.title ?? '';

        if (!pagePath || !productId || !productName) {
            return null;
        }

        return [
            {
                pageType: eventLocation,
                pagePath,
                productId,
                productName,
            },
        ];
    }, [eventLocation, product?.id, product?.title, router.asPath]);

    const eventAction = LOYALTY_RESTRICTION_EVENT_ACTION[product?.price?.loyaltyRestrictionHint?.type!];
    const loyaltyRestrictionType = product?.price?.loyaltyRestrictionHint?.type!;

    const handleLoyaltyRestrictionIconInView = useCallback(
        (inView: boolean) => {
            if (!inView || !eventContext) {
                return;
            }

            sendGtagEvent({
                event: EventNames.OWOX,
                eventCategory: EventCategory.NonInteractions,
                eventAction,
                eventLabel: LOYALTY_RESTRICTION_EVENT_LABELS[loyaltyRestrictionType].ICON_PDP_SHOW,
                eventContext,
            });
        },
        [eventAction, eventContext, loyaltyRestrictionType],
    );

    const handleLoyaltyRestrictionIconClick = useCallback(() => {
        if (!eventContext) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction,
            eventLabel: LOYALTY_RESTRICTION_EVENT_LABELS[loyaltyRestrictionType].ICON_PDP_CLICK,
            eventContext,
        });
    }, [eventAction, eventContext, loyaltyRestrictionType]);

    const handleLoyaltyRestrictionPopupShow = useCallback(() => {
        if (!eventContext) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.NonInteractions,
            eventAction,
            eventLabel: LOYALTY_RESTRICTION_EVENT_LABELS[loyaltyRestrictionType].POPUP_PDP_SHOW,
            eventContext,
        });
    }, [eventAction, eventContext, loyaltyRestrictionType]);

    const handleLoyaltyRestrictionPopupClose = useCallback(() => {
        if (!eventContext) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Interactions,
            eventAction,
            eventLabel: LOYALTY_RESTRICTION_EVENT_LABELS[loyaltyRestrictionType].POPUP_PDP_CLOSE,
            eventContext,
        });
    }, [eventAction, eventContext, loyaltyRestrictionType]);

    return {
        eventContext,
        handleLoyaltyRestrictionIconInView,
        handleLoyaltyRestrictionIconClick,
        handleLoyaltyRestrictionPopupShow,
        handleLoyaltyRestrictionPopupClose,
    };
};
